import React, { useState, useEffect } from 'react';
import { Paper, Text, Button, Stack, Group, Collapse, Divider, ActionIcon, Badge } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconEdit } from '@tabler/icons-react';
import './AppointmentList.css';
import moment from 'moment';
import { useCalendar } from '../../../../../context/CalendarContext';
import AppointmentListItem from './AppointmentListItem';

function AppointmentList({ 
  events, 
  onClickEditAppointment, 
  onStatusChange,
  selectedEvent
}) {
  const { selectedDate, selectedView } = useCalendar();
  const [expandedId, setExpandedId] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({
    'Needs Accepting': false,
    'Upcoming': false,
    'Past': true
  });
  const [groupedAppointments, setGroupedAppointments] = useState({
    'Needs Accepting': [],
    'Upcoming': [],
    'Past': []
  });

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const groupAppointments = (appointments) => {
    const now = moment();
    const startOfView = moment(selectedDate).startOf(selectedView === 'day' ? 'day' : 'week');
    const endOfView = moment(selectedDate).endOf(selectedView === 'day' ? 'day' : 'week');

    // Filter appointments to only show those within the current view
    const viewAppointments = appointments.filter(apt => {
      const aptDate = moment(apt.start);
      return aptDate.isBetween(startOfView, endOfView, null, '[]');
    });

    return {
      'Needs Accepting': viewAppointments.filter(apt => 
        apt.status === 'Requested'
      ),
      'Upcoming': viewAppointments.filter(apt => 
        (apt.status === 'Accepted' || apt.status === 'Confirmed') && 
        moment(apt.end).isAfter(now)
      ),
      'Past': viewAppointments.filter(apt => 
        (moment(apt.end).isBefore(now) || 
        apt.status === 'Completed' || 
        apt.status === 'No Show') &&
        apt.status !== 'Cancelled'
      ).sort((a, b) => new Date(b.end) - new Date(a.end))
    };
  };

  // Update groupedAppointments when appointments or view changes
  useEffect(() => {
    const apptsOnly = events.filter(evnt => !evnt?.isTimeBlock);
    setGroupedAppointments(groupAppointments(apptsOnly));
  }, [events, selectedDate, selectedView]);

  // Add effect to handle selected event changes
  useEffect(() => {
    if (selectedEvent) {
      setExpandedId(selectedEvent.id);
      
      // Ensure the section containing this appointment is expanded
      const section = Object.entries(groupedAppointments).find(([_, appointments]) =>
        appointments.some(apt => apt.id === selectedEvent.id)
      );
      
      if (section) {
        setCollapsedSections(prev => ({
          ...prev,
          [section[0]]: false
        }));
      }
    }
  }, [selectedEvent]);

  return (
    <Stack spacing="md">
      {Object.entries(groupedAppointments).map(([status, statusAppointments]) => 
        (statusAppointments.length > 0 || status !== 'Needs Accepting') ? (
          <div key={status}>
            <Group 
              position="apart" 
              className="section-header" 
              onClick={() => toggleSection(status)}
              style={{ cursor: 'pointer' }}
            >
              <Text>
                {status} ({statusAppointments.length})
              </Text>
              <ActionIcon>
                {collapsedSections[status] ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
              </ActionIcon>
            </Group>
            <Collapse in={!collapsedSections[status]}>
              <Stack spacing="sm" mt="sm">
                {statusAppointments.map(appointment => (
                  <AppointmentListItem
                    key={appointment.id}
                    appointment={appointment}
                    expandedId={expandedId}
                    selectedEvent={selectedEvent}
                    toggleExpand={toggleExpand}
                    onClickEditAppointment={onClickEditAppointment}
                    onStatusChange={onStatusChange}
                  />
                ))}
              </Stack>
            </Collapse>
          </div>
        ) : null
      )}
    </Stack>
  );
}

export default AppointmentList; 